import React, { useContext } from "react";
import { observer } from "mobx-react";
import SentimentIcon from "Components/common/SentimentIcon";
import { outboxStoreData } from "../outboxStore";
import { OutboxContext } from "../main";
import inboxV2Store from "./inboxV2Store";
import Avatar, { getInitials, getContactName } from "./Avatar";

const ContactRailRow = observer(
  ({ item = {}, isActive = false, onToggleImportant = () => {} }) => {
    const { firstName = "", lastName = "", email = "" } =
      item?.contact?.[0] || {};
    const { readStatus = "READ", sentiment = null, important = false } =
      item?.task || {};
    const isUnread = readStatus === "UNREAD";

    return (
      <div
        className={`inboxV2ContactRow ${isActive ? "active" : ""} ${
          isUnread ? "unread" : ""
        }`}
        onClick={() => inboxV2Store.selectContact(item)}
      >
        <Avatar initials={getInitials(firstName, lastName, email)} />
        <div className="inboxV2ContactMeta">
          <div className="inboxV2ContactName">
            <span title={getContactName(firstName, lastName, email)}>
              {getContactName(firstName, lastName, email)}
            </span>
            {isUnread && <i className="inboxV2UnreadDot" />}
          </div>
          <div className="inboxV2ContactEmail" title={email}>
            {email}
          </div>
        </div>
        <div className="inboxV2ContactRight">
          <button
            type="button"
            className={`inboxV2ImportantBtn ${important ? "on" : ""}`}
            title={important ? "Remove from important" : "Mark as important"}
            onClick={event => {
              event.stopPropagation();
              onToggleImportant(item);
            }}
          >
            <i className="material-icons">
              {important ? "star" : "star_border"}
            </i>
          </button>
          <span className="inboxV2Sentiment">
            <SentimentIcon sentiment={sentiment} />
          </span>
        </div>
      </div>
    );
  }
);

// Compact contact column shown once a contact is open -- the wide table is
// OutboxCardList, rendered by InboxV2 when nothing is selected. Pagination lives in
// InboxV2 below all three panes, not here.
const ContactRail = observer(() => {
  const {
    inputValues = {},
    setInputValues = () => {},
    getReloadOutboxCardData = () => {}
  } = useContext(OutboxContext);
  const { result = [] } = inputValues?.outboxData || {};
  const selectedId = inboxV2Store.getContactId(
    inboxV2Store?.selectedContact || {}
  );

  // Mark-as-important toggles on the contact's replied task -- optimistic flip, then the
  // shared markAsImportant endpoint with a silent reload to reconcile (mirrors OutboxCard).
  const toggleImportant = item => {
    const taskId = item?.task?.id;
    if (!taskId) return;
    const next = !item?.task?.important;
    setInputValues(prev => ({
      ...prev,
      outboxData: {
        ...prev.outboxData,
        result: (prev.outboxData?.result || []).map(row =>
          row?.task?.id === taskId
            ? { ...row, task: { ...row.task, important: next } }
            : row
        )
      }
    }));
    outboxStoreData.markAsImportant(taskId, next, () =>
      getReloadOutboxCardData(true)
    );
  };

  return (
    <div className="inboxV2ContactRail">
      <div className="inboxV2RailHeader">
        <button
          type="button"
          className="inboxV2RailBack"
          title="Back to Inbox"
          onClick={() => inboxV2Store.clearSelection()}
        >
          <i className="material-icons">arrow_back</i>
        </button>
        <span className="inboxV2SectionLabel">Contacts</span>
        {result.length > 0 && (
          <span className="inboxV2RailCount">{result.length}</span>
        )}
      </div>
      <div className="inboxV2RailList">
        {result.map((item, index) => {
          const contactId = item?.contact?.[0]?.id || "";
          return (
            <ContactRailRow
              key={item?.task?.id || `contactRow${index}`}
              item={item}
              isActive={!!contactId && contactId === selectedId}
              onToggleImportant={toggleImportant}
            />
          );
        })}
      </div>
    </div>
  );
});

export default ContactRail;
